import { ApartmentModel } from "@/types/models/apartment";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "@inertiajs/react";
import { Fragment, useEffect, useRef } from "react";
import { register } from "swiper/element/bundle";
import { SwiperProps } from "swiper/react";
import Container from "../Grid/Container";

register();

const getFormat = (filename: string) => filename.split(".").pop();

const resolveStatusBadge = (status: string) => {
    switch (status) {
        case "Wolne":
            return {
                label: "Dostępne",
                className: "bg-[#1E7200] border-[#1E7200] text-white",
            };
        case "Zarezerwowane":
        case "Wstępnie Zarezerwowany":
        case "WstepnieZarezerwowany":
            return {
                label: "Zarezerwowane",
                className: "bg-i-red border-i-red text-white",
            };
        case "Sprzedane":
            return {
                label: "Sprzedane",
                className: "bg-text border-text text-white",
            };
        default:
            return {
                label: status,
                className: "bg-secondary border-secondary text-white",
            };
    }
};

const RecommendedApartmentCard = ({ apartment }: { apartment: ApartmentModel }) => {
    const status = resolveStatusBadge(apartment.status);
    const floorCount = Math.max(10, Number(apartment.floor) || 0) + 1;
    const detailsUrl = route("frontend.apartments.details", {
        apartment: apartment.api_id,
    });

    return (
        <div className="bg-white border border-secondary rounded-lg overflow-hidden flex flex-col h-full w-[416px] max-w-[calc(100vw-2rem)]">
            <div className="flex items-stretch w-full">
                <div className="border-r border-secondary flex flex-col items-center shrink-0 w-[86px]">
                    <p className="text-p leading-p text-text font-extrabold tracking-p text-center pt-4 pb-2">
                        Piętro
                    </p>
                    <div className="flex flex-col items-center gap-3 pt-4 pb-10 w-full">
                        {Array.from({ length: floorCount }, (_, i) => (
                            <p
                                key={i}
                                className={`text-p leading-p tracking-p text-center size-8 flex items-center justify-center rounded-full ${i === Number(apartment.floor)
                                    ? "text-primary border border-secondary shadow-[0px_4px_4px_rgba(0,64,83,0.15)]"
                                    : "text-text"
                                    }`}
                            >
                                {i}
                            </p>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col flex-1 min-w-0 self-stretch">
                    <div className="flex flex-col gap-2 p-4 shrink-0">
                        <p className="text-h4 leading-h4 text-text tracking-[0.6px] uppercase">
                            Mieszkanie {apartment.name}
                        </p>
                        <span
                            className={`inline-flex items-center justify-center px-4 py-3 rounded-[32px] border text-4 leading-4 font-extrabold tracking-[0.35px] uppercase w-fit ${status.className}`}
                        >
                            {status.label}
                        </span>
                        <div className="flex flex-col">
                            {apartment.status !== "Sprzedane" && (
                                <p className="text-p leading-p tracking-p text-primary">
                                    Cena:{" "}
                                    <span className="text-text">
                                        {new Intl.NumberFormat("pl-PL").format(
                                            apartment.priceforapartment,
                                        )}{" "}
                                        zł
                                    </span>
                                </p>
                            )}
                            <p className="text-p leading-p tracking-p text-primary">
                                Powierzchnia:{" "}
                                <span className="text-text">
                                    {apartment.area} m<sup>2</sup>
                                </span>
                            </p>
                            <p className="text-p leading-p tracking-p text-primary">
                                Liczba pokoi:{" "}
                                <span className="text-text">{apartment.numberofrooms}</span>
                            </p>
                        </div>
                    </div>

                    {apartment.get_attachment?.layout ? (
                        <div className="relative flex-1 min-h-0 w-full">
                            <div className="absolute inset-0 flex items-center justify-center p-3">
                                <picture className="flex h-full max-h-full max-w-full items-center justify-center">
                                    <source
                                        srcSet={`${apartment.get_attachment.layout.replace(/\.[^.]+$/, "")}.webp`}
                                        type="image/webp"
                                    />
                                    <source
                                        srcSet={`${apartment.get_attachment.layout.replace(/\.[^.]+$/, "")}.${getFormat(apartment.get_attachment.layout)}`}
                                        type={`image/${getFormat(apartment.get_attachment.layout)}`}
                                    />
                                    <img
                                        className="max-h-full max-w-full object-contain"
                                        src={apartment.get_attachment.layout}
                                        alt={`Rzut mieszkania ${apartment.name}`}
                                        loading="lazy"
                                    />
                                </picture>
                            </div>
                        </div>
                    ) : (
                        <div className="relative flex-1 min-h-0 w-full">
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Icon icon="clarity:image-line" width={120} className="text-secondary" />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex gap-4 items-start justify-center px-4 py-4">
                <a
                    href={`${detailsUrl}#contact-form`}
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = `${detailsUrl}#contact-form`;
                    }}
                    className="button-small bg-secondary border-secondary text-white text-center px-9 py-3 leading-[16px]"
                >
                    Zapytaj o to
                    <br />
                    mieszkanie
                </a>
                {apartment.get_attachment?.apartment_card ? (
                    <a
                        href={apartment.get_attachment.apartment_card}
                        target="_blank"
                        rel="noreferrer"
                        className="button-small bg-[#39A7C8] border-[#39A7C8] text-white text-center px-9 py-3 leading-[16px]"
                    >
                        Pobierz
                        <br />
                        kartę lokalu
                    </a>
                ) : (
                    <span className="button-small bg-[#39A7C8]/60 border-[#39A7C8]/60 text-white text-center px-9 py-3 leading-[16px] opacity-50 cursor-not-allowed">
                        Pobierz
                        <br />
                        kartę lokalu
                    </span>
                )}
            </div>

            <div className="bg-secondary h-px opacity-50 w-full" />

            <a
                href={detailsUrl}
                className="flex gap-2 items-center justify-center px-4 py-5 text-primary text-p leading-label font-bold tracking-p uppercase"
            >
                Szczegóły
                <Icon icon="cil:magnifying-glass" width={24} height={24} />
            </a>
        </div>
    );
};

const RecommendedApartments = ({
    apartments,
}: {
    apartments: ApartmentModel[];
}) => {
    const swiperRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (!apartments?.length || !swiperRef.current) {
            return;
        }

        // Loop + centeredSlides + slidesPerView:"auto" breaks with few slides
        // (asymmetric gaps / clipped cards). Enable loop only when there are
        // enough cards to fill both sides of the viewport reliably.
        const enableLoop = apartments.length >= 7;

        const swiperContainer = swiperRef.current;
        const params: SwiperProps = {
            slidesPerView: "auto",
            spaceBetween: 24,
            grabCursor: true,
            speed: 500,
            loop: enableLoop,
            centeredSlides: true,
            centeredSlidesBounds: !enableLoop,
            watchOverflow: true,
            initialSlide: enableLoop ? 0 : Math.floor(apartments.length / 2),
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
            },
            navigation: {
                prevEl: ".recommended-apartments-prev",
                nextEl: ".recommended-apartments-next",
            },
        };

        // @ts-ignore
        Object.assign(swiperContainer, params);
        // @ts-ignore
        swiperContainer.initialize();
    }, [apartments]);

    if (!apartments?.length) {
        return null;
    }

    return (
        <section className="py-14 lg:py-18 mb-14 lg:mb-26 overflow-hidden">
            <Container>
                <h2 className="text-h2 leading-h2 text-primary font-extrabold tracking-heading uppercase text-center mb-10 lg:mb-12">
                    Rekomendowane mieszkania
                </h2>
            </Container>

            <div className="w-full overflow-hidden">
                {/* @ts-ignore */}
                <swiper-container ref={swiperRef} init="false">
                    {apartments.map((apartment) => (
                        <Fragment key={apartment.api_id}>
                            {/* @ts-ignore */}
                            <swiper-slide class="!w-auto">
                                <RecommendedApartmentCard apartment={apartment} />
                                {/* @ts-ignore */}
                            </swiper-slide>
                        </Fragment>
                    ))}
                    {/* @ts-ignore */}
                </swiper-container>
            </div>

            <div className="hidden maxMd:flex justify-center gap-4 mt-6">
                <button
                    type="button"
                    className="recommended-apartments-prev glass-style px-6 py-3 rounded-[60px]"
                    aria-label="Poprzednie mieszkanie"
                >
                    <Icon icon="oui:arrow-left" className="text-primary" width={32} height={32} />
                </button>
                <button
                    type="button"
                    className="recommended-apartments-next glass-style px-6 py-3 rounded-[60px]"
                    aria-label="Następne mieszkanie"
                >
                    <Icon icon="oui:arrow-right" className="text-primary" width={32} height={32} />
                </button>
            </div>

            <Container>
                <div className="flex justify-center mt-10 lg:mt-12">
                    <Link href={route("frontend.apartments")} className="button-primary max-w-fit text-center">
                        Zobacz wszystkie mieszkania
                    </Link>
                </div>
            </Container>
        </section>
    );
};

export default RecommendedApartments;
